import React from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Msg #992 — Admin: full-featured edit modal for another user's profile.
// Msg #993 — Adds picture-URL slots (up to 20) so admins can rebuild a user's
// avatar / gallery just like the user's own Edit Profile screen.
// Fetches from /api/profiles/get, saves via /api/admin/profiles/set.
export default function AdminEditProfileModal({ open, onClose, target, requester }) {
  const EMPTY_PICS = React.useMemo(() => Array.from({ length: 20 }, () => ''), []);
  const [description, setDescription] = React.useState('');
  const [gender, setGender] = React.useState('');
  const [fontColor, setFontColor] = React.useState('#111111');
  const [pictures, setPictures] = React.useState(EMPTY_PICS);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (!open || !target) return;
    setErr(''); setNote(''); setBusy(false);
    (async () => {
      try {
        const r = await axios.get(`${API}/profiles/get`, {
          params: { username: target.name },
        });
        const p = r.data?.profile || {};
        setDescription(p.description || target.description || '');
        setGender(p.gender || target.gender || '');
        setFontColor(p.font_color || target.color || '#111111');
        const arr = Array.isArray(p.pictures) && p.pictures.length
          ? p.pictures
          : (p.picture ? [p.picture] : []);
        const seeded = Array.from({ length: 20 }, (_, i) => arr[i] || '');
        setPictures(seeded);
      } catch (e) {
        setDescription(target.description || '');
        setGender(target.gender || '');
        setFontColor(target.color || '#111111');
        setPictures(EMPTY_PICS);
      }
    })();
  }, [open, target, EMPTY_PICS]);

  if (!open || !target) return null;

  const updatePic = (idx, val) => {
    setPictures((prev) => {
      const next = prev.slice();
      next[idx] = val;
      return next;
    });
  };

  const save = async () => {
    setBusy(true); setErr(''); setNote('');
    try {
      const cleanedPics = pictures.map((p) => (p || '').trim());
      await axios.post(`${API}/admin/profiles/set`, {
        requester,
        target: target.name,
        description,
        gender: gender || null,
        font_color: /^#[0-9a-fA-F]{6}$/.test(fontColor) ? fontColor : null,
        picture: cleanedPics[0] || '',
        pictures: cleanedPics,
      });
      setNote(`Saved ${target.name}'s profile.`);
      setTimeout(onClose, 900);
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Save failed.');
    } finally { setBusy(false); }
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 1300,
    background: 'rgba(20,40,20,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const shell = {
    width: 'min(96vw, 620px)', maxHeight: '90vh', overflowY: 'auto',
    background: '#f2e8c8', border: '2px solid #7a5a1a',
    padding: '20px 24px', fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#3a2b14', position: 'relative',
  };
  const row = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' };
  const label = { minWidth: 110, fontSize: 12, fontWeight: 'bold' };
  const input = { flex: 1, padding: '4px 6px', border: '1px solid #5a4a1d', fontFamily: 'inherit', fontSize: 12 };

  const previewSrcs = pictures.filter((p) => (p || '').trim()).slice(0, 6);

  return (
    <div style={overlay} data-testid="admin-edit-profile-modal" onClick={onClose}>
      <div style={shell} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="admin-edit-close-x"
          style={{
            position: 'absolute', top: 6, right: 8,
            width: 26, height: 26, background: '#c0392b', color: '#fff',
            border: '1px solid #7a1f14', borderRadius: 4,
            fontWeight: 'bold', fontSize: 14, cursor: 'pointer',
          }}
        >✕</button>
        <div style={{ fontSize: 18, fontWeight: 'bold', textTransform: 'uppercase',
                      letterSpacing: 2, marginBottom: 6 }}>
          ✎ Admin: Edit Profile
        </div>
        <div style={{ fontSize: 12, marginBottom: 12, fontStyle: 'italic', opacity: 0.85 }}>
          Editing <b>{target.name}</b>'s profile. Saves via <code>/api/admin/profiles/set</code>.
        </div>
        <div style={row}>
          <span style={label}>Gender</span>
          <select
            data-testid="admin-edit-gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            style={input}
          >
            <option value="">(unchanged)</option>
            <option value="undisclosed">undisclosed</option>
            <option value="female">female</option>
            <option value="male">male</option>
            <option value="other">other</option>
          </select>
        </div>
        <div style={row}>
          <span style={label}>Font color</span>
          <input
            data-testid="admin-edit-color-hex"
            type="text" value={fontColor}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (/^#[0-9a-fA-F]{6}$/.test(v)) setFontColor(v.toLowerCase());
              else if (/^[0-9a-fA-F]{6}$/.test(v)) setFontColor('#' + v.toLowerCase());
              else setFontColor(v);
            }}
            style={{ ...input, flex: '0 0 120px', fontFamily: 'monospace' }}
            placeholder="#RRGGBB"
          />
          <label
            title="Open OS color wheel"
            style={{
              padding: '2px 8px', cursor: 'pointer',
              background: 'linear-gradient(45deg,#ff004d,#ffbe0b,#8ac926,#1982c4,#6a4c93,#ff004d)',
              color: '#fff', fontWeight: 'bold', fontSize: 11,
              textShadow: '0 0 2px rgba(0,0,0,0.9)', border: '1px solid #5a4a1d',
            }}
          >
            🎨
            <input
              data-testid="admin-edit-color-wheel"
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(fontColor) ? fontColor : '#111111'}
              onChange={(e) => setFontColor(e.target.value.toLowerCase())}
              style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
            />
          </label>
          <span style={{ color: fontColor, fontWeight: 'bold', padding: '0 6px' }}>Preview</span>
        </div>
        <div style={{ ...row, alignItems: 'flex-start' }}>
          <span style={label}>Description</span>
          <textarea
            data-testid="admin-edit-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="Character bio / description"
            style={{ ...input, resize: 'vertical', minHeight: 100 }}
          />
        </div>

        {/* Msg #993 — Picture URL slots (up to 20). Slot 0 doubles as main avatar. */}
        <div style={{ ...row, alignItems: 'flex-start' }}>
          <span style={label}>
            Picture URLs
            <div style={{ fontSize: 10, fontWeight: 'normal', color: '#5a4a1d', marginTop: 2 }}>
              (up to 20)
            </div>
          </span>
          <div style={{ flex: 1, minWidth: 0 }} data-testid="admin-edit-pictures-list">
            {pictures.map((val, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <input
                  data-testid={`admin-edit-picture-url-${i}`}
                  value={val || ''}
                  onChange={(e) => updatePic(i, e.target.value)}
                  placeholder={i === 0 ? 'https://... (main avatar)' : `https://... (extra ${i})`}
                  style={{
                    flex: 1, padding: '3px 6px', border: '1px solid #5a4a1d',
                    fontFamily: 'inherit', fontSize: 11, minWidth: 0,
                  }}
                />
                {val && (
                  <button
                    type="button"
                    data-testid={`admin-edit-picture-clear-${i}`}
                    onClick={() => updatePic(i, '')}
                    style={{
                      backgroundColor: '#5d2a2a', color: '#fce2e2',
                      border: '1px solid #3a1a1a', fontSize: 10,
                      padding: '2px 6px', cursor: 'pointer',
                    }}
                  >×</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {previewSrcs.length > 0 && (
          <div
            data-testid="admin-edit-picture-preview"
            style={{ textAlign: 'center', marginTop: 6, marginBottom: 10, display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}
          >
            {previewSrcs.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`preview ${i + 1}`}
                style={{ maxWidth: 84, maxHeight: 84, border: '1px solid #7a5a1a' }}
                onError={(e) => (e.target.style.display = 'none')}
              />
            ))}
          </div>
        )}

        {err && <div data-testid="admin-edit-error" style={{ color: '#7a1f1f', fontSize: 12, marginBottom: 6 }}>{err}</div>}
        {note && <div data-testid="admin-edit-note" style={{ color: '#1c5a1c', fontSize: 12, marginBottom: 6 }}>{note}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ background: '#5a4a1d', color: '#f5e6c0',
                     border: '1px solid #3a2b14', padding: '4px 14px',
                     fontWeight: 'bold', cursor: 'pointer' }}
          >Cancel</button>
          <button
            data-testid="admin-edit-save"
            onClick={save}
            disabled={busy}
            style={{ background: '#4a3a1a', color: '#ffe1a0',
                     border: '1px solid #a08a30', padding: '4px 14px',
                     fontWeight: 'bold', cursor: busy ? 'wait' : 'pointer',
                     opacity: busy ? 0.7 : 1 }}
          >{busy ? 'Saving…' : 'SAVE'}</button>
        </div>
      </div>
    </div>
  );
}
